import { useEffect, useRef } from "react";

export interface ShortcutsSheetProps {
  open: boolean;
  onClose: () => void;
}

interface Group {
  title: string;
  rows: Array<[string, string]>;
}

/**
 * 快捷操作浮层（Wiki G-10 / 需求 §11）。
 *
 * 原型把快捷键说明收在一个可发现的浮层里，而不是常驻顶部横条——
 * 常驻说明会占掉内容高度，而且用户看两遍就不看了。
 */
export const SHORTCUT_GROUPS: Group[] = [
  {
    title: "视图 / 内容",
    rows: [
      ["1", "故事板"],
      ["2", "画布"],
      ["3", "时间线"],
      ["L", "资料库"],
      ["O", "大纲"],
      ["S", "剧本"],
      ["D", "数据"],
    ],
  },
  {
    title: "Agent",
    rows: [
      ["A", "展开 / 收起 Agent"],
      ["/", "聚焦 Agent 输入框"],
      ["Esc", "关闭弹层"],
    ],
  },
  {
    title: "画布",
    rows: [
      ["滚轮 / 双指", "上下平移"],
      ["Shift + 滚轮", "横向平移"],
      ["Ctrl / ⌘ + 滚轮", "以光标为中心缩放"],
      ["+ / −", "放大 / 缩小"],
      ["0", "适应画面"],
      ["Shift + 点击镜头", "加入 / 移出生成队列"],
      ["拖动镜头", "只改布局，不改顺序"],
      ["拖动场块标题", "整组移动"],
      ["双击镜头", "回故事板看这一镜"],
    ],
  },
  {
    title: "故事板",
    rows: [
      ["← / →", "切换镜头"],
      ["空格", "确认当前镜"],
    ],
  },
];

export function ShortcutsSheet({ open, onClose }: ShortcutsSheetProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="dp-scrim" role="presentation" onClick={onClose}>
      <div
        className="dp-dialog dp-shortcuts-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="快捷操作"
        ref={ref}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="dp-sheet-head">
          <div>
            <p className="dp-sheet-kicker">⌘ 快捷操作</p>
            <h2 className="dp-sheet-title">键盘与鼠标</h2>
            <p className="dp-sheet-sub">弹层打开时快捷键不生效，先把浮层关掉</p>
          </div>
          <button type="button" className="dp-btn sm" onClick={onClose}>
            关闭
          </button>
        </header>

        <div className="dp-shortcuts-grid">
          {SHORTCUT_GROUPS.map((group) => (
            <section key={group.title} className="dp-shortcuts-group">
              <h3>{group.title}</h3>
              <dl>
                {group.rows.map(([keys, label]) => (
                  <div key={keys}>
                    <dt>
                      <kbd>{keys}</kbd>
                    </dt>
                    <dd>{label}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
